// components/Admin/RemindersTable.jsx
import React, { useState } from "react";
import { Table, Badge, Button } from "react-bootstrap";
import QuickAppointmentModal from "./QuickAppointmentModal";
import { useNavigate } from "react-router-dom";

const RemindersTable = ({ reminders, AuthToken, onRefresh }) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedReminder, setSelectedReminder] = useState(null);
    const navigate = useNavigate();

    // Helper function to map design aesthetics for status badges
    const getStatusBadge = (status) => {
        const variants = {
            pending: "warning",
            completed: "success",
            missed: "danger",
            cancelled: "secondary",
        };
        return variants[status] || "primary";
    };

    // Helper function to turn "2026-05-21 22:21:20" into "21 May 2026"
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);

        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();

        return `${day} ${month} ${year}`;
    };

    // Handle opening quick appointment modal
    const handleQuickCreate = (reminder) => {
        setSelectedReminder(reminder);
        setShowModal(true);
    };

    // Handle modal close
    const handleModalClose = () => {
        setShowModal(false);
        setSelectedReminder(null);
    };

    // Handle success
    const handleSuccess = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    const handleViewAppointment = (id, procedureName) => {
        navigate(`/appointments/${id}`, {
            state: {
                procedureName: procedureName || null
            }
        });
    };

    return (
        <>
            <div className="table-responsive">
                <Table hover align="middle" className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Appointment ID</th>
                            <th>Type</th>
                            <th>Reminder Date & Status</th>
                            <th>Customer</th>
                            <th>Pet</th>
                            <th>Doctor</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reminders.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="text-center py-4 text-muted">
                                    No contextual reminders found matching these parameters.
                                </td>
                            </tr>
                        ) : (
                            reminders.map((item, index) => (
                                <tr key={`${item.id}-${index}`}>
                                    <td className="fw-bold">{item.appointment_number}</td>
                                    <td>
                                        <span className="text-capitalize">
                                            {item.type_label}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="fw-semibold">{formatDate(item.reminder_date)}</div>

                                        {/* Conditional Status Line */}
                                        {item.type !== "followup" && item.type !== "checkup" && (
                                            <div className="mt-1">
                                                <Badge bg={getStatusBadge(item.reminder_status)} className="text-capitalize">
                                                    {item.reminder_status}
                                                </Badge>
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <div className="fw-bold">{item.customer_name}</div>
                                        <small className="text-muted d-block">{item.customer_phone}</small>
                                    </td>
                                    <td>
                                        <div className="fw-bold">{item.pet_name}</div>
                                        <small className="text-muted text-capitalize">
                                            Breed: {item.pet_breed?.name || "N/A"}
                                        </small>
                                    </td>
                                    <td><strong>DR. </strong>{item.doctor_name}</td>
                                    <td>
                                        <Button
                                            variant="info"
                                            size="sm"
                                            className="me-2 d-flex align-items-center"
                                            onClick={() => handleViewAppointment(item.appointment_id , item.type )}
                                            title="View Appointment"
                                        >
                                            <i className="material-icons" style={{ fontSize: "16px" }}>
                                                visibility
                                            </i>
                                        </Button>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={() => handleQuickCreate(item)}
                                            title="Quick create appointment"
                                            className="d-flex align-items-center"
                                        >
                                            <i className="material-icons" style={{ fontSize: "16px" }}>done</i>
                                            <span className="ms-1">complete</span>
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Quick Appointment Modal */}
            <QuickAppointmentModal
                show={showModal}
                onHide={handleModalClose}
                reminder={selectedReminder}
                AuthToken={AuthToken}
                onSuccess={handleSuccess}
            />
        </>
    );
};

export default RemindersTable;